import { tgxApi } from "../../../app/tgxApi";
import {
    getAuthorizationHeader,
    getFileDownloadHeaders,
    handleQueryError,
    handleQueryErrorAndSuccess
} from "../../../helpers/utils/RtkQueryUtils";
import { getFileName, serialize } from "../../../helpers/utils/Utils";
import { downloadFile, openFileInFrame, openFileInNewTab } from "../../../helpers/utils/FileUtils";

export const collectorsApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({
        searchCollector: build.query({
            query: (data) => {
                // Serialize the data to query parameters
                let paramData = serialize(data);
                return {
                    url: `/ajax/collectors?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['collectors'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        createCollector: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/create/collector`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['collectors'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Collector")
            },
        }),
        getCollectorById: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-collector-by-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['collectorById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),


        getNotesByCollectorId: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-collector-note-by-collector-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['collectorNotes'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        updateCollector: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/update/collector/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['collectors', 'collectorById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Collector")
            },
        }),
        createConfiguration: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/create/collector-config`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['getCollectorConfig'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Collector")
            },
        }),
        createCollectorNoteService: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/create-collector-note/${id}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['collectorNotes', 'collectorNotesById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Collector")
            },
        }),
        updateConfiguration: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/update/collector-config`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['getCollectorConfig', 'getCollectorConfigById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Collector")
            },
        }),
        getCollectorConfig: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-collector-config-by-collector-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['getCollectorConfig'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        deleteCollectorConfigByIdService: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/delete-collector-config-by-configId/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),

                };
            },
            invalidatesTags: ['getCollectorConfig', 'getCollectorConfigById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Collector")
            },
        }),
        deleteCollectorById: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/delete-collector-by-id/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader(),

                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Collector")
            },
        }),
        getCollectorDocuments: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-collector-document-list/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['collectorDocumentsById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getCollectorLogs: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-collector-logs-by-collector-id/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['collectorLogsById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        exportCollectorData: build.query({
            query: (query) => {
                return {
                    url: `/export/collector?`,
                    method: 'POST',
                    params: query,
                    headers: getFileDownloadHeaders(),
                    responseType: 'arraybuffer',
                    responseHandler: async (response: { blob: () => any; }) => {
                        const blob = await response.blob();
                        downloadFile(blob, getFileName("collectors"))
                        return { success: true };
                    },
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        checkJobExist: build.query({
            query: ({ id, fileId, jobType }) => {
                return {
                    url: `/ajax/get/collector-job/${id}/${fileId}/${jobType}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['collectorLogsById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        createFileJob: build.mutation({
            query: ({collectorId, fileId, jobType, year}) => {
                return {
                    url: `/ajax/create/collector-file-job/${collectorId}/${fileId}/${jobType}/${year}`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Collector file job")
            },
        }),
        viewCollectorFile: build.query({
            query: ({ fileId, isDownload, isOpenInFrame, fileSize, fileName }) => {
                return {
                    url: `/ajax/download-collector-file/${fileId}/${fileSize}?fileName=${fileName}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                    responseHandler: async (response: any) => {
                        const blob = await response.blob();
                        if (!isOpenInFrame) {
                            const contentDisposition = response.headers.get('Content-Disposition');
                            const filename = contentDisposition
                                ? contentDisposition.split('filename=')[1]
                                : `file_${fileId}.pdf`;

                            isDownload ? downloadFile(blob, filename) : openFileInNewTab(blob)
                        } else {
                            openFileInFrame(blob);
                        }
                        return { success: true };
                    },
                };

            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
       
    }),
});


export const {
    useSearchCollectorQuery,
    useCreateCollectorMutation,
    useGetCollectorByIdQuery,
    useUpdateCollectorMutation,
    useCreateConfigurationMutation,
    useCreateCollectorNoteServiceMutation,
    useGetCollectorLogsQuery,
    useGetCollectorDocumentsQuery,
    useUpdateConfigurationMutation,
    useGetCollectorConfigQuery,
    useLazyExportCollectorDataQuery,
    useDeleteCollectorConfigByIdServiceMutation,
    useGetNotesByCollectorIdQuery,
    useLazyViewCollectorFileQuery,
    useDeleteCollectorByIdMutation,
    useCreateFileJobMutation
} = collectorsApi;